// @flow
import React, { Component } from 'react'
import ReactSelect from 'react-select'
import { translateFunc, translations } from '../../shared/localize'
import _ from 'lodash'
import classNames from 'classnames'

import type { Public } from '../types'
import { OrtSelectComponents, OrtSelectStyles } from '../controls/Select'
import getJson from '../../services/getJson'

const t = translateFunc(translations.audience_edit.loadable_select)

type Props = {|
  currentAdAccountId: number,
  selectedValue?: Object,
  loadUrl: string,
  refreshTokenUrl: string,
  onChange: (Object) => void,
  selectOptions: Object
|}

type ReactSelectOptions = {|
  value: any,
  label: string,
  className?: string
|}

type State = {|
  isLoaded: boolean,
  isLoading: boolean,
  isFailed: boolean,
  refreshToken: boolean,
  options: Array<Object>
|}

export default class LoadableSelect extends Component<Props, State> {
  Option: Function

  static defaultProps = {
    selectOptions: {}
  }

  constructor(props: Props) {
    super(props)

    this.state = {
      isLoaded: false,
      isLoading: false,
      isFailed: false,
      refreshToken: false,
      options: []
    }

    this.Option = (props) => {
      return (
        <OrtSelectComponents.Option {...props}>
          {this.renderOption(props.data)}
        </OrtSelectComponents.Option>
      )
    }
  }

  mapResponseKeys(_value: any, key: any) {
    switch (key) {
      case 'id':
        return 'value'
      default:
        return key
    }
  }

  revertMapKeys(_value: any, key: any) {
    switch (key) {
      case 'value':
        return 'id'
      default:
        return key
    }
  }

  componentValueChanged(selectedObject: Public) {
    const { onChange } = this.props

    onChange && onChange(_.mapKeys(selectedObject, this.revertMapKeys))
  }

  loadOptions() {
    if (this.state.isLoaded) return

    this.setState({ isLoading: true, isFailed: false, refreshToken: false }, () => {
      getJson(this.props.loadUrl, {
        current_ad_account_id: this.props.currentAdAccountId.toString()
      })
        .then(({ data }) => {
          if (data.refresh_token) {
            this.setState({
              isLoaded: false,
              isLoading: false,
              isFailed: true,
              refreshToken: true
            })
          } else {
            this.setOptions(data)
          }
        })
        .fail(() => {
          this.setState({
            isLoaded: false,
            isLoading: false,
            isFailed: true
          })
        })
    })
  }

  setOptions(options: Array<ReactSelectOptions>) {
    this.setState({
      isLoaded: true,
      isLoading: false,
      options: _.map(options, (object) => _.mapKeys(object, this.mapResponseKeys))
    })
  }

  renderSelectOptions() {
    const { isLoaded, options } = this.state

    if (!isLoaded) return this.renderLoadingOptions()

    return this.renderOptions(options)
  }

  renderOptions(options: Array<ReactSelectOptions>) {
    if (options.length === 0) {
      return [{ label: t('no_found_publics'), value: 'no_found_publics', disabled: true }]
    }

    return options
  }

  renderLoadingOptions(): Array<Object> {
    const { selectedValue } = this.props
    const { isFailed, refreshToken } = this.state

    const currentWithRightKeys = selectedValue && _.mapKeys(selectedValue, this.mapResponseKeys)

    if (isFailed) {
      let label = ''
      let value = ''
      if (refreshToken) {
        label = t('refresh_token')
        value = 'refresh_token'
      } else {
        label = t('failed')
        value = 'failed'
      }
      return _.reject(
        [currentWithRightKeys, { label: label, value: value, disabled: true }],
        _.isEmpty
      )
    }

    return _.reject(
      [currentWithRightKeys, { label: t('loading'), value: 'loading', disabled: true }],
      _.isEmpty
    )
  }

  renderOption(option: ReactSelectOptions) {
    if (option.value === 'failed') return this.renderReloadOption(option)
    if (option.value === 'refresh_token') return this.renderRefreshTokenOption()

    return <span className={classNames(option.className)}>{option.label}</span>
  }

  // uses inline css style because of ReactSelect
  renderReloadOption(option: ReactSelectOptions) {
    return (
      <span
        style={{
          color: 'red',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
        onClick={() => this.loadOptions()}
      >
        {option.label}
        <button
          className="btn btn-default"
          style={{
            marginLeft: 20,
            paddingTop: 2,
            paddingBottom: 2
          }}
        >
          {t('reload_btn')}
        </button>
      </span>
    )
  }

  renderRefreshTokenOption() {
    return (
      <span className="t-RefreshTokenLink">
        {t('refresh_token_message')}
        <a target="_blank" href={this.props.refreshTokenUrl}>
          {t('refresh_token_link_text')}
        </a>
      </span>
    )
  }

  render() {
    const { selectedValue, selectOptions } = this.props
    const { isLoading } = this.state

    return (
      <ReactSelect
        onMenuOpen={() => this.loadOptions()}
        options={this.renderSelectOptions()}
        isLoading={isLoading}
        noOptionsMessage={() => t('no_results')}
        styles={OrtSelectStyles}
        components={{ ...OrtSelectComponents, DropdownIndicator: () => null, Option: this.Option }}
        onChange={(value) => this.componentValueChanged(value)}
        value={selectedValue}
        isOptionDisabled={(option) => option.disabled}
        getOptionValue={(option) => option.value}
        {...selectOptions}
      />
    )
  }
}
